import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Route } from 'next';
import { useSelector } from 'react-redux';
import { useCookies } from 'react-cookie';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import classNames from 'classnames/bind';

import { RootState } from 'redux/store';

import routes from 'components/Header/Header.data';
import { RouteType } from 'components/Header/Header.types';

import useWindowSize from 'utils/hooks/useWindowSize';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import { css, isObjectEmpty } from 'utils/helpers/functions';

import { LinksGroup } from 'components/Header/LinksGroup'
import { AdminLinks } from 'components/Header/AdminLinks';
import { UserDropdown } from 'components/Header/UserDropdown';

import styles from 'components/Header/Header.module.scss';

const cn = classNames.bind(styles);

const Header = (): ReactElement => {
  const { logged, user: userStore } = useSelector((state: RootState) => state.userData);
  const [{ user }] = useCookies(['user']);
  const router = useRouter();

  const [isUserPopupActive, setIsUserPopupActive] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = React.useState<number | null>(null);
  const [navigationLinks] = useState<RouteType[]>(routes);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const userDropElement = useRef<HTMLUListElement>(null);
  const header = useRef<HTMLInputElement>(null);

  const { width } = useWindowSize();
  const isMobile = width !== 0 && width <= 860;

  useEffect(() => {
    setIsUserPopupActive(false);
  }, [router.pathname]);

  const changeSelectedItem = (index: number | null) => {
    setSelectedItem(index);
  };

  useEffect(() => {
    if (user || userStore) {
      if (user) {
        setIsAdmin(Boolean(user.isAdmin));
      }
      if (userStore) {
        setIsAdmin(Boolean(userStore.isAdmin));
      }
    }
    if (isObjectEmpty(user) || logged) return;
  }, [user, logged]);

  useEffect(() => {
    css(document.body, { overflow: isMobile ? 'hidden' : 'visible' });
  }, [isMobile]);

  useOnClickOutside(header as React.RefObject<HTMLElement>, () => {
    setIsUserPopupActive(false);
  });

  const handleUserPopup = useCallback(() => {
    setIsUserPopupActive((prevState) => !prevState);
  }, []);

  const navigationElements = useMemo(() => {
    return navigationLinks.map((item, index) => {
      if (item.group || item.admin) {
        if ((!logged && item.label === 'svg') || (item.admin && !isAdmin)) return null;

        return (
          <li
            className={cn('header__nav-item', { 'isActive': globalThis.window?.location.pathname === item.link })}
            key={`${item.label}_${index}`}
          >
            {!item.admin ? (
              <LinksGroup
                changeSelectedItem={changeSelectedItem}
                index={index}
                label={item.label}
                isMobile={isMobile}
                isActive={index === selectedItem}
                groupLink={item.group}
                link={item.link}
                setIsUserPopupActive={() => setIsUserPopupActive(false)}
              />
            ) : (
              <AdminLinks
                index={index}
                isActive={index === selectedItem}
                changeSelectedItem={changeSelectedItem}
                isMobile={isMobile}
              />
            )}
          </li>
        );
      }

      return (
        <li
          className={cn('header__nav-item', { 'isActive': router.pathname === item.link })}
          key={`${item.label}_${index}`}
          onClick={() => setIsUserPopupActive(false)}
        >
          <Link href={item.link as Route}>{item.label}</Link>
        </li>
      );
    });
  }, [navigationLinks, logged, user, userStore, router.pathname, isMobile, selectedItem]);

  return (
    <header
      className={cn('header')}
      ref={header}
    >
      <div className={cn('header__body', 'container')}>
        <Link
          className={cn('header__logo')}
          href="/"
        >
          <Image
            width={118}
            height={23}
            src="/images/global/logo-dfa-light.svg"
            alt="Digital Freight Alliance"
          />
        </Link>

        <nav className={cn('header__nav', { 'isOpen': isMobile && isUserPopupActive })}>
          <ul className={cn('header__nav-list')}>{navigationElements}</ul>
          <ul
            className={cn('header__nav-list')}
            ref={userDropElement}
          >
            {logged ? (
              <>
                <li
                  className={cn('header__nav-item', 'header__nav-item-right')}
                  onClick={handleUserPopup}
                >
                  <span className={cn('header__nav__user-icon', { 'active': isUserPopupActive })} />
                </li>
                <UserDropdown
                  isUserPopupActive={isUserPopupActive}
                  isMobile={isMobile}
                />
              </>
            ) : (
              <li className={cn('header__nav-item', { 'mobileActiveLink': router.pathname === '/auth/sign-in' })}>
                <Link href="/auth/sign-in">Sign In</Link>
              </li>
            )}
          </ul>
        </nav>

        {isMobile && (
          <div
            className={cn('header__burger-container')}
            onClick={() => setIsUserPopupActive(!isUserPopupActive)}
          >
            <div className={cn('burger', { 'isOpen': isUserPopupActive })} />
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
